import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Mail, CheckCircle2, Clock, Send, Sparkles } from "lucide-react";
import { PageHeader, fmtDateTime, Empty } from "../../components/admin/ui";

const OnboardingDrips = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/onboarding-drips"),
      api.get("/admin/onboarding-drips/stats"),
    ])
      .then(([list, st]) => { setItems(list.data.items || []); setStats(st.data || {}); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => items.filter((m) =>
    !q || (m.email || "").toLowerCase().includes(q.toLowerCase()) || (m.name || "").toLowerCase().includes(q.toLowerCase())
  ), [items, q]);

  return (
    <div className="space-y-8" data-testid="admin-onboarding-drips">
      <PageHeader
        overline="Onboarding"
        title={<>The <span className="italic gold-text">10-email</span> drip</>}
        subtitle="Every member who joins the Portal walks through a 30-day welcome — prayer, Telegram, mentorship, events, and giving. Track delivery here."
      />

      {stats && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="drips-stats">
          {[
            { label: "Members in drip",  value: stats.members_in_drip,  icon: Sparkles },
            { label: "Emails delivered", value: stats.emails_delivered, icon: CheckCircle2 },
            { label: "Emails scheduled", value: stats.emails_scheduled, icon: Clock },
          ].map((s) => (
            <div key={s.label} className="bento-card !p-6">
              <span className="glow-blob" />
              <div className="flex items-center gap-3">
                <span className="icon-badge !h-10 !w-10"><s.icon className="h-5 w-5 text-[#C9A44C]" /></span>
                <div className="overline">{s.label}</div>
              </div>
              <div className="text-4xl font-light text-[#F6F0E6] mt-3">{s.value ?? 0}</div>
            </div>
          ))}
        </section>
      )}

      <section className="bento-card !p-5">
        <span className="glow-blob" />
        <input
          className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-4 py-2 text-sm text-[#F6F0E6] focus:outline-none w-full"
          placeholder="Search by email or name…"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="drips-search"
        />
      </section>

      {loading ? (
        <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty title="No drips yet" subtitle="As members sign up for the Portal, their 10-email onboarding flow appears here." />
      ) : (
        <ul className="space-y-3">
          {filtered.map((m) => {
            const isOpen = expanded === m.email;
            const pct = Math.round((m.sent_count / Math.max(m.total_steps, 1)) * 100);
            return (
              <li key={m.email} className="bento-card !p-5" data-testid={`drip-row-${m.email}`}>
                <span className="glow-blob" />
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : m.email)}
                  className="w-full flex items-center justify-between gap-4 text-left"
                  data-testid={`drip-toggle-${m.email}`}
                >
                  <div className="min-w-0">
                    <div className="font-serif-display text-lg text-[#F6F0E6] truncate">{m.name || m.email}</div>
                    <div className="text-xs text-[#D9D9D9]/65 truncate">{m.email} · started {fmtDateTime(m.started_at)}</div>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    <div className="text-right">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{m.sent_count}/{m.total_steps} sent</div>
                      <div className="w-40 h-1.5 mt-1.5 rounded-full bg-white/8 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#C9A44C] to-[#F28C18]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <ol className="mt-5 pt-5 border-t border-[#C9A44C]/12 space-y-2" data-testid={`drip-steps-${m.email}`}>
                    {m.steps.map((s, i) => {
                      const sent = (s.send_at || "9999") <= new Date().toISOString();
                      return (
                        <li key={i} className="flex items-start gap-3">
                          {sent ? <CheckCircle2 className="h-4 w-4 text-[#C9A44C] mt-0.5 shrink-0" />
                                : <Clock className="h-4 w-4 text-[#D9D9D9]/60 mt-0.5 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#F6F0E6] truncate">{s.subject}</div>
                            <div className="text-[11px] text-[#D9D9D9]/55">
                              {sent ? "Sent" : "Scheduled"} · {fmtDateTime(s.send_at)} · +{s.hours}h
                              {s.resend_id ? <span className="ml-2 text-[#C9A44C]/70">id:{String(s.resend_id).slice(0,8)}…</span> : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OnboardingDrips;
